import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import SourcesService from "@/api/services/sources";
import { useToast } from "@/providers/ToastProvider";
import ExistingSource, { ExistingSourceTestPayload, PreparedSource } from "@/types/sources";

export function useSource(sourceId: string) {
  return useQuery({
    queryKey: ["sources", sourceId],
    queryFn: () => SourcesService.getSource(sourceId),
  });
}

export function useSources() {
  return useQuery({
    queryKey: ["sources"],
    queryFn: () => SourcesService.getSources(),
  });
}

export function useSourceForm(orgID?: string) {
  return useQuery({
    queryKey: ["sources", "form", orgID],
    queryFn: () => SourcesService.getSourceForm(orgID),
  });
}

export function useSourceVendors() {
  return useQuery({
    queryKey: ["sources", "vendors"],
    queryFn: () => SourcesService.getSourceVendors(),
  });
}

export function useTestNewSource() {
  return useMutation({
    mutationFn: (source: PreparedSource) => SourcesService.testNewSource(source),
  });
}

export function useTestExistingSource() {
  return useMutation({
    mutationFn: (payload: ExistingSourceTestPayload) => SourcesService.testExistingSource(payload),
  });
}

export function useCreateSource() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { source: PreparedSource; redirectPath?: string }) =>
      SourcesService.postSource(payload.source),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Source created successfully!`,
      });
    },
  });
}

export function useUpdateSource() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { sourceId: ExistingSource["id"]; source: Partial<PreparedSource>; redirectPath?: string }) =>
      SourcesService.patchSource(payload.sourceId, payload.source),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      queryClient.invalidateQueries({
        queryKey: ["sources", variables.sourceId],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Source updated successfully!`,
      });
    },
  });
}

export function useDeleteSource() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { sourceId: ExistingSource["id"]; redirectPath?: string }) =>
      SourcesService.deleteSource(payload.sourceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Source deleted successfully!`,
      });
    },
  });
}

export function useGenerateSourcePublicKey() {
  return useMutation({
    mutationFn: (orgID?: string) => SourcesService.generateSourcePublicKey(orgID),
  });
}
